import {
  SHIFT_DURATION_PRESETS,
  SHIFT_MINUTE_STEPS,
  formatShiftEndTime,
  formatShiftLength,
  minutesToParts,
  partsToMinutes,
} from "../lib/shift-duration";

/** Hours + presets instead of raw minutes — patrol ops think in shift length, not minute counts. */
export function ShiftDurationPicker({
  durationMinutes,
  localStartTime,
  onChange,
  disabled = false,
}: {
  durationMinutes: number;
  localStartTime: string;
  onChange: (minutes: number) => void;
  disabled?: boolean;
}) {
  const { hours, minutes } = minutesToParts(durationMinutes);
  const endLabel = formatShiftEndTime(localStartTime, durationMinutes);

  function setParts(nextHours: number, nextMinutes: number): void {
    onChange(partsToMinutes(nextHours, nextMinutes));
  }

  return (
    <fieldset className="tpl__fieldset shift-duration" disabled={disabled}>
      <legend className="tpl__legend">Shift length</legend>
      <p className="setup__field-hint">
        Pick a common patrol length or set hours and minutes. We store minutes behind the scenes.
      </p>
      <div className="shift-duration__presets" role="group" aria-label="Common shift lengths">
        {SHIFT_DURATION_PRESETS.map((preset) => (
          <button
            key={preset.minutes}
            type="button"
            className={`toggle-chip${durationMinutes === preset.minutes ? " toggle-chip--on" : ""}`}
            title={preset.title}
            aria-pressed={durationMinutes === preset.minutes}
            onClick={() => onChange(preset.minutes)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="shift-duration__custom">
        <label className="tpl__field">
          Hours
          <select
            value={hours}
            onChange={(event) => setParts(Number(event.target.value), minutes)}
            aria-label="Shift hours"
          >
            {Array.from({ length: 25 }, (_, index) => (
              <option key={index} value={index}>
                {index === 1 ? "1 hour" : `${index} hours`}
              </option>
            ))}
          </select>
        </label>
        <label className="tpl__field">
          Minutes
          <select
            value={minutes}
            onChange={(event) => setParts(hours, Number(event.target.value))}
            aria-label="Shift minutes"
          >
            {SHIFT_MINUTE_STEPS.map((step) => (
              <option key={step} value={step}>
                {step === 0 ? "0 min" : `${step} min`}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="shift-duration__summary" role="status">
        <span className="shift-duration__summary-length">{formatShiftLength(durationMinutes)}</span>
        {endLabel !== null && durationMinutes > 0 ? (
          <span className="shift-duration__summary-end"> · ends about {endLabel}</span>
        ) : null}
      </p>
    </fieldset>
  );
}
